import React, { useEffect, useState } from "react"
import { Card, CardBody, Button, Input, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap"
import { get } from "helpers/api_helper"
import { settingsTime } from "./maintenanceForm"
import useSearchRebuild, { searchStage } from "./useSearchRebuild"
import SearchHistory from "./SearchHistory"

export default function AccountSearchIndex({ actorId }) {
  const [read, setRead] = useState({ loading: true }), [refresh, setRefresh] = useState(0)
  const [review, setReview] = useState(false), [reason, setReason] = useState("")
  const operation = useSearchRebuild({ actorId, onSettled: () => setRefresh(value => value + 1) })
  const { resume } = operation
  useEffect(() => {
    let current = true, timer
    setRead({ loading: true })
    const load = async () => {
      try {
        const { data } = await get("/account-search/status", { timeout: 5000 })
        if (!data || !("active" in data) || !("lastCompleted" in data)) throw new Error("Invalid search status")
        if (current) {
          setRead({ data })
          if (data.active) { resume(data.active); timer = setTimeout(load, 5000) }
        }
      } catch { if (current) setRead({ error: true }) }
    }
    void load()
    return () => { current = false; clearTimeout(timer) }
  }, [actorId, refresh, resume])
  const pending = operation.pending ? operation.receipt : read.data?.active
  const busy = operation.pending || !!read.data?.active
  const unresolved = read.data?.writes?.unconfirmed || 0
  return <Card><CardBody><section aria-label="Account search index">
    <h5>Account search index</h5>
    <p className="text-muted">Rebuild missing or outdated account search data. Account changes normally update search individually.</p>
    <p className="text-muted">The existing index remains searchable while its replacement is prepared. Account data, ranking calculations and points are unchanged.</p>
    {read.loading && <p role="status">Loading search index status…</p>}
    {read.error && <div role="alert"><p>Search index status could not be loaded.</p><Button onClick={() => setRefresh(value => value + 1)}>Retry search status</Button></div>}
    {read.data && <p>Last verified rebuild: <strong>{read.data.lastCompleted ? settingsTime(read.data.lastCompleted.completedAt) : "None recorded"}</strong></p>}
    {unresolved > 0 && <div role="alert" className="system-settings__history">
      <strong>{unresolved} account search {unresolved === 1 ? "update has" : "updates have"} an unconfirmed outcome.</strong>
      <p>Rebuilding is blocked until technical support verifies these updates. Existing search remains available. Repeated rebuild requests will not resolve this condition.</p>
      <div>Oldest unconfirmed update: {settingsTime(read.data.writes.oldestAt)}</div>
      <Button size="sm" className="mt-2" onClick={() => setRefresh(value => value + 1)}>Recheck search updates</Button>
    </div>}
    {busy && <div className="system-settings__history" role="status">
      <strong>{searchStage(pending?.stage)}</strong>
      {Number.isSafeInteger(pending?.indexedAccounts) && <div>Accounts indexed: {pending.indexedAccounts}</div>}
      {pending?.actorName && <div>Requested by {pending.actorName}</div>}
      <p className="mb-0 mt-2">You can leave this page. The recorded result will be checked when you return.</p>
    </div>}
    {operation.message && <p className="mt-3" role={operation.status === "completed" ? "status" : "alert"}>{operation.message}</p>}
    {operation.status === "unknown" && <Button className="mt-2" onClick={operation.retry}>Check / resume rebuild</Button>}
    <div className="system-settings__actions"><Button color="primary" disabled={!read.data || busy || unresolved > 0 || operation.status === "denied"} onClick={() => { setReason(""); setReview(true) }}>Review rebuild</Button></div>
    <Modal isOpen={review} toggle={() => setReview(false)} className="system-settings-dialog">
      <ModalHeader toggle={() => setReview(false)}>Review account search rebuild</ModalHeader>
      <ModalBody><p>A replacement index will be built from current accounts and checked before activation. Search keeps using the existing index during preparation.</p>
        <p>This does not recalculate rankings or points. The request and its result will be recorded in the action logs.</p>
        <Label for="search-rebuild-reason">Rebuild reason *</Label><Input id="search-rebuild-reason" type="textarea" maxLength={1000} value={reason} onChange={event => setReason(event.target.value)} aria-describedby="search-rebuild-reason-help" />
        <p id="search-rebuild-reason-help" className="text-muted mt-1 mb-0">At least 3 characters.</p>
      </ModalBody>
      <ModalFooter><Button onClick={() => setReview(false)}>Cancel</Button><Button color="primary" disabled={reason.trim().length < 3 || busy} onClick={() => { if (operation.start(reason.trim())) setReview(false) }}>Confirm rebuild</Button></ModalFooter>
    </Modal>
    <SearchHistory refresh={refresh} />
  </section></CardBody></Card>
}
